import { BreifInfluencerV2StatusEnum } from "@entities/brief/lib/constants";
import { ISearchFacets } from "@shared/lib/types/briefInfluencerV2";
import { CampaignBriefStage } from "./campaignBriefStage";
import { InfluencerColumnStatusFilterValue } from "./influencerColumnStatusFilter";

type NumericFacetKey = keyof {
  [K in keyof ISearchFacets]: ISearchFacets[K] extends number ? K : never;
};

const BRAND_COMMERCIAL_FACET_KEYS: Record<string, NumericFacetKey> = {
  isPendingQuote: "isInitialBrandCostPending",
  isNegotiating: "isNegotiating",
  isOfferReceived: "isOfferReceived",
};

const INFLUENCER_COMMERCIAL_FACET_KEYS: Record<string, NumericFacetKey> = {
  isInfluencerCommercialPending: "isInfluencerCommercialPending",
  isInfluencerCommercialNegoRequested: "isInfluencerCommercialNegoRequested",
  isInfluencerCommercialReceived: "isInfluencerCommercialReceived",
  isInfluencerCommercialNegoReceived: "isInfluencerCommercialNegoReceived",
};

const COLUMN_STATUS_FACET_KEYS: Partial<
  Record<InfluencerColumnStatusFilterValue, NumericFacetKey>
> = {
  creatorDeclined: "isCreatorDeclined",
  brandDeclined: "isBrandDeclined",
  onboardingPending: "isCreatorOnboardPending",
  contactNotFound: "isPrimaryContactNotAdded",
  deliverablesPending: "isDeliverablesPending",
  awaitingCommercials: "isInitialInfluencerCostPending",
  awaitingBrandPrice: "isInitialBrandCostPending",
  confirmationPending: "confirmationPending",
};

export function getCampaignBriefStageCounts(
  facets?: ISearchFacets
): Record<CampaignBriefStage, number> {
  return {
    [CampaignBriefStage.Reviewing]: facets?.inReview ?? 0,
    [CampaignBriefStage.Shortlisted]: facets?.inShortlisting ?? 0,
    [CampaignBriefStage.Purchased]:
      facets?.status?.find(
        (status) => status._id === BreifInfluencerV2StatusEnum.Purchased
      )?.count ?? 0,
  };
}

export function getNumericFacetCount(
  facets: ISearchFacets | undefined,
  key: NumericFacetKey
): number | undefined {
  if (!facets) {
    return undefined;
  }
  const value = facets[key];
  return typeof value === "number" ? value : undefined;
}

export function getBrandCommercialFilterCount(
  facets: ISearchFacets | undefined,
  filterValue: string
): number | undefined {
  const facetKey = BRAND_COMMERCIAL_FACET_KEYS[filterValue];
  return facetKey ? getNumericFacetCount(facets, facetKey) : undefined;
}

export function getInfluencerCommercialFilterCount(
  facets: ISearchFacets | undefined,
  filterValue: string
): number | undefined {
  const facetKey = INFLUENCER_COMMERCIAL_FACET_KEYS[filterValue];
  return facetKey ? getNumericFacetCount(facets, facetKey) : undefined;
}

export function getInfluencerColumnStatusFilterCount(
  facets: ISearchFacets | undefined,
  filterValue: InfluencerColumnStatusFilterValue
): number | undefined {
  if (!facets) {
    return undefined;
  }
  if (filterValue === "purchased") {
    return (
      facets.status.find(
        (status) => status._id === BreifInfluencerV2StatusEnum.Purchased
      )?.count ?? 0
    );
  }
  const facetKey = COLUMN_STATUS_FACET_KEYS[filterValue];
  return facetKey ? getNumericFacetCount(facets, facetKey) : undefined;
}

export function formatFilterLabelWithCount(
  label: string,
  count: number | undefined
): string {
  if (count === undefined) {
    return label;
  }
  return `${label} (${count})`;
}
